import React, { startTransition, useEffect, useState } from 'react';
import { Box, Paper, TextField, Tooltip, Typography } from '@mui/material';
import styles from './DocumentDetailsBlock.module.scss';
import DocxIcon from '../../../../../assets/svg/docx_icon.svg?react';
import PdfIcon from '../../../../../assets/svg/pdf_icon.svg?react';
import BKIcon from '../../../../../assets/svg/bk_icon.svg?react';
import { useTranslation } from 'react-i18next';
import { DocumentDetailsBlockProps } from './types';
import { SelectValueType } from '../../types';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import { useDocumentVersions } from '../../hooks/useDocumentVersions';
import { useParams, useSearchParams } from 'react-router-dom';
import MessageModal from '../../../../common/message-modal/MessageModal';
import parse from 'html-react-parser';
import ButtonDropDown from '../../../../common/button-drop-down/ButtonDropDown';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import InfoIcon from '@mui/icons-material/Info';

const DocumentDetailsBlock = ({
  documentDetails,
  profileLabelsArray,
  workflowsArray,
  typeLabels,
  propertiesEditMode,
  documentTypeLabel,
  setDocumentTypeLabel,
  documentWorkflowLabel,
  setDocumentWorkflowLabel,
  assigneeLabel,
  setAssigneeLabel,
  fileName,
  setFileName,
  category,
  setCategory,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  subscription,
  setSubscription,
  signed,
  setSigned,
  frequency,
  setFrequency,
  customLabel,
  setCustomLabel,
  currency,
  setCurrency,
  amount,
  setAmount,
  frequencyValuesArray,
  booleanValuesArray,
  currencyValuesArray,
  categoryArray,
}: DocumentDetailsBlockProps) => {
  const { t } = useTranslation();
  const { documentId } = useParams();
  const [params] = useSearchParams();
  const contractGenerated = params.get('contractGenerated');
  const [currentProfileLabels, setCurrentProfileLabels] = useState<SelectValueType[]>([]);

  useEffect(() => {
    if (Array.isArray(profileLabelsArray)) {
      const profilesOptions = [
        ...[{ value: '', label: t('labels.noAssignee') }],
        ...profileLabelsArray,
      ];
      startTransition(() => setCurrentProfileLabels(profilesOptions));
    }
  }, [category, profileLabelsArray, t]);

  const {
    versionHTML,
    setVersionHTML,
    //getDocumentUploadHTML,
    pendingStatus: pendingStatusVersion,
    setVersionModalOpened,
    versionModalOpened,
    //versionsArray,
  } = useDocumentVersions({
    id: Number(documentId),
    isDocumentContract: Boolean(contractGenerated),
    allowedGettingVersions: Boolean(documentDetails),
  });
  //const { checkedLatestVersion, changeLatestVersionProp } = useLatestVersion();

  const renderFileTypeIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return (
          <Tooltip title={t('fileTypes.pdf')} arrow placement='top'>
            <span>
              <PdfIcon />
            </span>
          </Tooltip>
        );
      case 'docx':
        return (
          <Tooltip title={t('fileTypes.msWord')} arrow placement='top'>
            <span>
              <DocxIcon />
            </span>
          </Tooltip>
        );
      case 'doc':
        return (
          <Tooltip title={t('fileTypes.msWord')} arrow placement='top'>
            <span>
              <DocxIcon />
            </span>
          </Tooltip>
        );
      case 'bk':
        return (
          <Tooltip title={t('fileTypes.businessKitz')} arrow placement='top'>
            <span>
              <BKIcon />
            </span>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Box className={styles.container}>
        <Box className={styles.sectionHeader}>
          <Typography variant='h6' className={styles.sectionTitle}>
            {t('headers.properties')}
          </Typography>
        </Box>
        <Paper className={styles.propertiesContainer} elevation={0}>
          <Box className={styles.propertyItem}>
            <TextField
              fullWidth
              label={t('labels.fileName')}
              variant='outlined'
              size='medium'
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              disabled={!propertiesEditMode}
              slotProps={{
                input: {
                  endAdornment: (
                    <Box component='span' sx={{ display: 'flex', alignItems: 'center' }}>
                      {renderFileTypeIcon(
                        documentDetails
                          ? 'du_filetype' in documentDetails
                            ? (documentDetails.du_filetype?.[0] ?? 'bk')
                            : 'bk'
                          : ''
                      )}
                    </Box>
                  ),
                },
              }}
            />
          </Box>
          <Box className={styles.inputs_container}>
            <Box className={styles.propertyItem}>
              <ButtonDropDown
                id='profile'
                label={t('labels.profile')}
                options={categoryArray}
                disabled={!propertiesEditMode}
                onChange={(event) => {
                  setCategory(event.target.value);
                  setAssigneeLabel('');
                  setDocumentWorkflowLabel('');
                }}
                value={category}
              />
            </Box>
            <Box className={styles.propertyItem}>
              <ButtonDropDown
                id='documentDetailsBlockAssignToDropdown'
                label={t('labels.assignTo')}
                options={currentProfileLabels}
                disabled={!propertiesEditMode || currentProfileLabels.length === 0}
                onChange={(event) => setAssigneeLabel(event.target.value)}
                value={assigneeLabel}
              />
            </Box>
            <Box className={styles.propertyItem}>
              <ButtonDropDown
                id='documentDetailsBlockDocumentWorkflowDropdown'
                label={t('labels.documentWorkflow')}
                options={[
                  ...[{ value: '', label: t('labels.generalUnassigned') }],
                  ...workflowsArray,
                ]}
                disabled={!propertiesEditMode}
                onChange={(event) => setDocumentWorkflowLabel(event.target.value)}
                value={documentWorkflowLabel}
              />
            </Box>
            <Box className={styles.propertyItem}>
              <ButtonDropDown
                id='documentDetailsBlockDocumentTypeDropdown'
                label={t('labels.documentType')}
                options={typeLabels}
                disabled={!propertiesEditMode}
                onChange={(event) => setDocumentTypeLabel(event.target.value)}
                value={documentTypeLabel}
              />
            </Box>
          </Box>
          <Box className={styles.propertyItem}>
            <TextField
              fullWidth
              label={t('labels.customLabel')}
              variant='outlined'
              size='medium'
              autoComplete='off'
              value={customLabel}
              onChange={(event) => setCustomLabel(event.target.value)}
              disabled={!propertiesEditMode}
              slotProps={{
                input: {
                  endAdornment: (
                    <Box component='div' sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title={t('messages.customLabelMessage')} arrow placement='top'>
                        <InfoIcon fontSize='small' color='primary' className={'info-icon'} />
                      </Tooltip>
                    </Box>
                  ),
                },
              }}
            />
          </Box>
        </Paper>
        <Typography variant='h6' sx={{ marginBottom: '8px' }} className={styles.sectionTitle}>
          {t('headers.extras')}
        </Typography>
        <Paper className={styles.propertiesContainer} sx={{ paddingBottom: 0 }} elevation={0}>
          <Box className={styles.inputs_container}>
            <Box className={styles.propertyItem}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en-gb'>
                <DemoContainer sx={{ padding: 0, overflow: 'visible' }} components={['DatePicker']}>
                  <DatePicker
                    className={styles.date_picker}
                    disabled={!propertiesEditMode}
                    value={startDate}
                    label={t('labels.startDate')}
                    maxDate={endDate ? endDate : undefined /*dayjs(new Date())*/}
                    format='DD.MM.YYYY'
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{
                      field: { clearable: true },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Box>
            <Box className={styles.propertyItem}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en-gb'>
                <DemoContainer sx={{ padding: 0, overflow: 'visible' }} components={['DatePicker']}>
                  <DatePicker
                    className={styles.date_picker}
                    disabled={!propertiesEditMode}
                    value={endDate}
                    label={t('labels.endRenewalDate')}
                    //maxDate={dayjs(new Date())}
                    minDate={startDate ? startDate : undefined}
                    format='DD.MM.YYYY'
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{
                      field: { clearable: true },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Box>

            <Box className={styles.propertyItem}>
              <TextField
                fullWidth
                type='number'
                label={t('labels.amount')}
                variant='outlined'
                size='medium'
                value={amount === '0.00' ? '' : amount}
                onKeyDown={(event) =>
                  ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault()
                }
                onChange={(event) => setAmount(event.target.value)}
                disabled={!propertiesEditMode}
                slotProps={{
                  input: {
                    endAdornment: (
                      <Box component='div' className='control-attached'>
                        <ButtonDropDown
                          id='documentDetailsBlockCurrencyDropdown'
                          label={t('labels.currency')}
                          options={currencyValuesArray}
                          disabled={!propertiesEditMode}
                          onChange={(event) => setCurrency(event.target.value)}
                          value={currency}
                        />
                      </Box>
                    ),
                  },
                  htmlInput: { min: 0 },
                }}
              />
            </Box>
            <Box className={styles.propertyItem}>
              <ButtonDropDown
                id='documentDetailsBlockSubscriptionDropdown'
                label={t('labels.subscription')}
                options={booleanValuesArray}
                disabled={!propertiesEditMode}
                onChange={(event) => setSubscription(event.target.value)}
                value={subscription}
              />
            </Box>

            <Box className={styles.propertyItem}>
              <ButtonDropDown
                id='documentDetailsBlockFrequencyDropdown'
                label={t('labels.frequency')}
                options={frequencyValuesArray}
                disabled={!propertiesEditMode}
                onChange={(event) => setFrequency(event.target.value)}
                value={frequency}
              />
            </Box>
            <Box className={styles.propertyItem}>
              <ButtonDropDown
                id='documentDetailsBlockSignedDropdown'
                label={t('labels.signed')}
                options={booleanValuesArray}
                disabled={!propertiesEditMode}
                onChange={(event) => setSigned(event.target.value)}
                value={signed}
              />
            </Box>
          </Box>
        </Paper>
        {/*!contractGenerated && (
          <Paper className={styles.propertiesContainer} elevation={0}>
            <Box
              className={styles.section}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Typography variant='h6' className={styles.sectionTitle}>
                {t('headers.versions')}
              </Typography>
              <div>
                {t('labels.useLatestVersion')}
                <Switch
                  id='documentDetailsBlockChangeLatestVersionPropSwitch'
                  checked={checkedLatestVersion}
                  slotProps={{ input: { 'aria-label': t('labels.useLatestVersion') } }}
                  onChange={() => changeLatestVersionProp()}
                />
              </div>
            </Box>
            <TableContainer>
              <Table className={styles.versionTable}>
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.versionTableHeader}>
                      {t('labels.version')}
                    </TableCell>
                    <TableCell className={styles.versionTableHeader}>
                      {t('labels.dateCreated')}
                    </TableCell>
                    <TableCell className={styles.versionTableHeader} align='right'>
                      {t('labels.action')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versionsArray.map((el, i) => (
                    <TableRow key={el.id} className={styles.versionTableRow}>
                      <TableCell>
                        {t('miscellaneousText.version') + ' ' + (versionsArray.length - i)}
                      </TableCell>
                      <TableCell>{Helpers.timeStampToDateV2(el.updated)}</TableCell>
                      <TableCell align='right' style={{ paddingRight: 0 }}>
                        <Button
                          id={`documentDetailsBlockViewContentBtn_${i}`}
                          onClick={() => {
                            setVersionModalOpened(true);
                            getDocumentUploadHTML(el.id);
                          }}
                          variant='contained'
                          className='btn-default btn-action-basic btn-small'
                        >
                          {t('buttons.viewContent')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )*/}
      </Box>
      {versionModalOpened && (
        <MessageModal
          fullWidth={true}
          open={versionModalOpened}
          onClose={() => {
            setVersionModalOpened(false);
            setVersionHTML(null);
          }}
          noActionButton={true}
          goBackButtonText={t('buttons.goBack')}
          modalContent={<div>{versionHTML ? parse(versionHTML) : null}</div>}
        />
      )}
      {pendingStatusVersion && <ProgressOverlay />}
    </>
  );
};

export default DocumentDetailsBlock;
